"use client";

import { createContext, useCallback, useContext, type ReactNode } from "react";
import type { UsageMode, UsageModeCapabilities } from "@louez/types";
import { DEFAULT_USAGE_MODE, usageModeCapabilities } from "@louez/types";
import { hasPermission, type MemberRole, type Permission } from "@louez/utils";

import { setStorefrontSlug } from "@/lib/orpc/client";
import { isDiscountDisplayable } from "@/lib/utils/util.discount-visibility";
import type { RentalPeriodRules } from "@/lib/utils/util.rental-period";

interface StoreContextValue {
  role?: MemberRole;
  storeId: string;
  currency: string;
  storeSlug: string;
  storeName: string;
  timezone?: string;
  maxDiscountPercent?: number | null;
  /**
   * Prefix of every storefront link on the current host, computed on the
   * server (`getStorefrontPathPrefix`): "" on the store subdomain, in
   * standalone and preview modes; "/{slug}" on the dashboard host. Undefined
   * outside the storefront (dashboard tree), where links keep the slug.
   */
  basePath?: string;
  /** What every period picker validates against; absent outside the storefront. */
  periodRules?: RentalPeriodRules;
  /**
   * How the renter uses Reserlo. Dashboard surfaces derive what they show
   * from this, so a menu item and the server that answers it cannot disagree.
   * Absent means the default, a store with a public shop.
   */
  usageMode?: UsageMode;
  /** The renter's own website in `external-website` mode; null otherwise. */
  externalWebsiteUrl?: string | null;
}

const StoreContext = createContext<StoreContextValue | undefined>(undefined);

interface StoreProviderProps {
  role?: MemberRole;
  children: ReactNode;
  storeId: string;
  currency: string;
  storeSlug: string;
  storeName: string;
  timezone?: string;
  maxDiscountPercent?: number | null;
  basePath?: string;
  periodRules?: RentalPeriodRules;
  usageMode?: UsageMode;
  externalWebsiteUrl?: string | null;
}

export const StoreProvider = ({
  role,
  children,
  storeId,
  currency,
  storeSlug,
  storeName,
  timezone,
  maxDiscountPercent,
  basePath,
  periodRules,
  usageMode,
  externalWebsiteUrl,
}: StoreProviderProps) => {
  // Set the store slug for the ORPC client synchronously during render,
  // so it's available before any child component makes API calls.
  setStorefrontSlug(storeSlug);

  return (
    <StoreContext.Provider
      value={{
        role,
        storeId,
        currency,
        storeSlug,
        storeName,
        timezone,
        maxDiscountPercent,
        basePath,
        periodRules,
        usageMode,
        externalWebsiteUrl,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = (): StoreContextValue => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
};

/** Dashboard permissions come from the server-resolved active membership. */
export const useStoreHasPermission = (permission: Permission): boolean => {
  const context = useContext(StoreContext);
  return context?.role ? hasPermission(context.role, permission) : false;
};

// Hook pour obtenir la devise avec fallback
export const useStoreCurrency = (): string => {
  const context = useContext(StoreContext);
  return context?.currency || "EUR";
};

export const useStoreTimezone = (): string | undefined => {
  const context = useContext(StoreContext);
  return context?.timezone;
};

export const useStoreMaxDiscountPercent = (): number | null | undefined => {
  const context = useContext(StoreContext);
  return context?.maxDiscountPercent;
};

/**
 * Server-computed link prefix of the storefront, or undefined when the
 * provider did not receive one (dashboard tree, tests). Safe outside a
 * provider so shared hooks can fall back to the slugged form.
 */
/** The store's period rules; only the storefront tree provides them. */
export const useStorePeriodRules = (): RentalPeriodRules => {
  const { periodRules } = useStore();
  if (!periodRules) {
    throw new Error("useStorePeriodRules must be used within the storefront providers");
  }
  return periodRules;
};

export const useStorefrontBasePath = (): string | undefined => {
  const context = useContext(StoreContext);
  return context?.basePath;
};

/**
 * Predicate for every storefront surface that advertises a markdown: badge,
 * strikethrough, discount row, "you save" line. Bound to the store's cap so
 * callers only pass the percentage they are about to show.
 */
export const useDiscountVisibility = (): ((
  reductionPercent: number | null | undefined,
) => boolean) => {
  const maxDiscountPercent = useStoreMaxDiscountPercent();
  return useCallback(
    (reductionPercent: number | null | undefined) =>
      isDiscountDisplayable(reductionPercent, maxDiscountPercent),
    [maxDiscountPercent],
  );
};

/**
 * What the store's usage mode allows, for client surfaces.
 *
 * The same definition the server gates on, so a hidden button and a refused
 * request always agree. Outside a provider it reports the default rather than
 * throwing: a shared component must not crash in the storefront tree.
 */
export const useUsageModeCapabilities = (): UsageModeCapabilities => {
  const context = useContext(StoreContext);
  return usageModeCapabilities(context?.usageMode ?? DEFAULT_USAGE_MODE);
};

/** The renter's own website when the mode offers one, else null. */
export const useExternalWebsiteUrl = (): string | null => {
  const context = useContext(StoreContext);
  if (!usageModeCapabilities(context?.usageMode ?? DEFAULT_USAGE_MODE).externalWebsiteLink) {
    return null;
  }
  return context?.externalWebsiteUrl?.trim() || null;
};
